import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

export default function LoginPage() {
  const { t } = useTranslation();
  const [authError, setAuthError] = useState(false);
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN || '';
  const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '';

  const handleAuth0Login = async () => {
    if (!auth0Domain || !auth0ClientId) {
      setAuthError(true);
      console.error('Missing Auth0 frontend configuration', {
        auth0Domain,
        auth0ClientId,
      });
      return;
    }

    try {
      await loginWithRedirect({
        authorizationParams: {
          redirect_uri: window.location.origin + '/auth/callback',
        },
      });
    } catch (error) {
      setAuthError(true);
      console.error('Auth0 login failed to redirect:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/scan', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const features = [
    t('auth.features.directive'),
    t('auth.features.risks'),
    t('auth.features.findings'),
    t('auth.features.history'),
  ];

  return (
    <div className="login-page">
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(0, 200, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 200, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          pointerEvents: 'none',
        }}
      />

      <div className="login-grid">
        <section className="login-hero">
          <div
            style={{
              position: 'absolute',
              top: '18%',
              left: '10%',
              width: 'min(45vw, 400px)',
              aspectRatio: '1 / 1',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(0,200,255,0.08) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />

          <div className="stagger" style={{ position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 40,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  background: 'var(--accent-primary)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  fontWeight: 900,
                  color: 'var(--bg-primary)',
                  fontFamily: 'var(--font-display)',
                  boxShadow: '0 0 24px rgba(0, 200, 255, 0.4)',
                }}
              >
                CC
              </div>
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 24,
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.02em',
                }}
              >
                ComplianceCheck
              </span>
            </div>

            <h1 className="page-title login-title">{t('auth.headline')}</h1>

            <p className="page-subtitle login-subtitle">{t('auth.subtitle')}</p>

            <ul className="login-feature-list">
              {features.map(feature => (
                <li key={feature} className="login-feature-item">
                  <span className="login-feature-check">✓</span>
                  {feature}
                </li>
              ))}
            </ul>

            <p className="login-urgency">{t('auth.urgency')}</p>

            <div className="login-regulation-tag">
              <span className="login-regulation-dot" />
              <span>{t('auth.regulationTag')}</span>
            </div>
          </div>
        </section>

        <aside className="login-panel">
          <div
            style={{
              position: 'absolute',
              bottom: '10%',
              right: '5%',
              width: 'min(34vw, 300px)',
              aspectRatio: '1 / 1',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(0,200,255,0.04) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />

          <div className="animate-fade-in" style={{ position: 'relative' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 28,
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: 8,
              }}
            >
              {t('auth.signIn')}
            </h2>
            <p
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                marginBottom: 36,
              }}
            >
              {t('auth.signInSubtitle')}
            </p>

            <div className="login-actions">
              <button
                type="button"
                onClick={() => navigate('/scan')}
                className="button"
              >
                {t('trial.firstScan')}
              </button>
              <p>{t('trial.noAccount')}</p>
              <button
                type="button"
                onClick={handleAuth0Login}
                className="button button-secondary"
              >
                {t('trial.signIn')}
              </button>
              {authError && <p role="alert">{t('trial.authError')}</p>}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
